import React from 'react';
import { MiniMapNodeProps } from 'reactflow';
import { widgetRegistry } from '../utils/widgetRegistry.js';
import { Widget } from '../types/index.js';

const categoryColors: Record<string, string> = {
  market: '#FFFFFF',
  analysis: '#007AFF',
  portfolio: '#34C759',
  data: '#616161',
  custom: '#424242',
};

const SELECTION_STROKE = '#7406F2';
const BASE_STROKE_WIDTH = 1;
const SELECTED_STROKE_WIDTH = 2;

export const MiniMapNode: React.FC<MiniMapNodeProps> = ({
  x,
  y,
  width,
  height,
  color,
  strokeColor,
  id,
  selected,
}) => {
  const widget = (window as any).__minimapNodeData?.[id] as Widget | undefined;
  const isSelected = selected || (window as any).__selectedNodes?.includes(id);
  const outlineColor = isSelected ? SELECTION_STROKE : strokeColor;
  const outlineWidth = isSelected ? SELECTED_STROKE_WIDTH : BASE_STROKE_WIDTH;

  const renderSelectionOverlay = () => {
    if (!isSelected) return null;
    return (
      <rect
        x={x - 3}
        y={y - 3}
        width={width + 6}
        height={height + 6}
        fill="none"
        stroke={SELECTION_STROKE}
        strokeWidth={BASE_STROKE_WIDTH}
        strokeDasharray="4 4"
        rx={Math.min(6, Math.max(2, Math.min(width, height) * 0.2))}
        ry={Math.min(6, Math.max(2, Math.min(width, height) * 0.2))}
        opacity={0.8}
      />
    );
  };

  if (!widget) {
    return (
      <g>
        {renderSelectionOverlay()}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={color}
          stroke={outlineColor}
          strokeWidth={outlineWidth}
          rx={4}
          ry={4}
        />
      </g>
    );
  }

  const widgetDef = widgetRegistry.get(widget.type);
  const category = widgetDef?.category || 'custom';
  const fillColor = categoryColors[category] || categoryColors.custom;
  const cornerRadius = Math.min(6, Math.max(2, Math.min(width, height) * 0.25));

  if (widget.type === 'widgetGroup') {
    return (
      <g>
        {renderSelectionOverlay()}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fillColor}
          stroke={outlineColor}
          strokeWidth={outlineWidth}
          rx={cornerRadius}
          ry={cornerRadius}
          opacity={0.9}
        />
        <rect
          x={x + 3}
          y={y + 3}
          width={width - 6}
          height={height - 6}
          fill="none"
          stroke={outlineColor}
          strokeWidth={BASE_STROKE_WIDTH}
          strokeDasharray="2 2"
          rx={Math.max(2, cornerRadius - 2)}
          ry={Math.max(2, cornerRadius - 2)}
        />
      </g>
    );
  }

  switch (category) {
    case 'analysis': {
      const chamfer = Math.min(width, height) * 0.2;
      return (
        <g>
          {renderSelectionOverlay()}
          <path
            d={`M ${x + chamfer} ${y} L ${x + width - chamfer} ${y} L ${x + width} ${y + chamfer} L ${x + width} ${y + height - chamfer} L ${x + width - chamfer} ${y + height} L ${x + chamfer} ${y + height} L ${x} ${y + height - chamfer} L ${x} ${y + chamfer} Z`}
            fill={fillColor}
            stroke={outlineColor}
            strokeWidth={outlineWidth}
            opacity={0.9}
          />
        </g>
      );
    }
    case 'data': {
      const cx = x + width / 2;
      const cy = y + height / 2;
      const size = Math.min(width, height) * 0.7;
      return (
        <g>
          {renderSelectionOverlay()}
          <rect
            x={cx - size / 2}
            y={cy - size / 2}
            width={size}
            height={size}
            fill={fillColor}
            stroke={outlineColor}
            strokeWidth={outlineWidth}
            transform={`rotate(45 ${cx} ${cy})`}
            opacity={0.9}
          />
        </g>
      );
    }
    default:
      return (
        <g>
          {renderSelectionOverlay()}
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={fillColor}
            stroke={outlineColor}
            strokeWidth={outlineWidth}
            rx={cornerRadius}
            ry={cornerRadius}
            opacity={0.9}
          />
        </g>
      );
  }
};
